;(() => {
  const positionStorageKey = 'vspots_geo_position_v3'
  const manualLocationStorageKey = 'vspots_geo_manual_location_v2'
  const citySelectionStorageKey = 'vspots_city_selection_v1'
  const positionLifetime = 7 * 24 * 60 * 60 * 1000
  const manualLocationLifetime = 30 * 24 * 60 * 60 * 1000
  const inlineSlidersByRoot = new WeakMap()

  const readStoredLocation = () => {
    try {
      const position = JSON.parse(window.localStorage.getItem(positionStorageKey))
      if (
        Number.isFinite(position?.latitude)
        && Number.isFinite(position?.longitude)
        && Number.isFinite(position?.createdAt)
        && Date.now() - position.createdAt < positionLifetime
      ) {
        return { ...position, source: 'geolocation' }
      }

      const manualLocation = JSON.parse(window.localStorage.getItem(manualLocationStorageKey))
      if (
        Number.isFinite(manualLocation?.latitude)
        && Number.isFinite(manualLocation?.longitude)
        && Number.isFinite(manualLocation?.createdAt)
        && Date.now() - manualLocation.createdAt < manualLocationLifetime
      ) {
        return { ...manualLocation, source: 'manual-location' }
      }
    } catch {
      return null
    }

    return null
  }

  const readSelectedCity = () => {
    const urlCity = new URL(window.location.href).searchParams.get('city')
    if (urlCity) return urlCity.trim().slice(0, 80)

    try {
      const selection = JSON.parse(window.localStorage.getItem(citySelectionStorageKey))
      return typeof selection?.city === 'string' ? selection.city.trim().slice(0, 80) : ''
    } catch {
      return ''
    }
  }

  const normalizedCityKey = (value) =>
    String(value || '')
      .normalize('NFKD')
      .replaceAll(/\p{Mark}/gu, '')
      .toLocaleLowerCase('de-DE')
      .replaceAll(/[^\p{Letter}\p{Number}]+/gu, ' ')
      .trim()

  const distanceInKilometers = (origin, destination) => {
    const earthRadius = 6371
    const toRadians = (degrees) => (degrees * Math.PI) / 180
    const latitudeDelta = toRadians(destination.latitude - origin.latitude)
    const longitudeDelta = toRadians(destination.longitude - origin.longitude)
    const originLatitude = toRadians(origin.latitude)
    const destinationLatitude = toRadians(destination.latitude)
    const haversine =
      Math.sin(latitudeDelta / 2) ** 2
      + Math.cos(originLatitude) * Math.cos(destinationLatitude) * Math.sin(longitudeDelta / 2) ** 2

    return earthRadius * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
  }

  const hasUsableDealCoordinates = (latitude, longitude) =>
    Number.isFinite(latitude)
    && Number.isFinite(longitude)
    && latitude >= 34
    && latitude <= 72
    && longitude >= -25
    && longitude <= 45

  const formatDistance = (distance) => {
    if (distance < 1) {
      return `${Math.max(100, Math.round((distance * 1000) / 100) * 100)} m`
    }

    return `${new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
      maximumFractionDigits: distance < 10 ? 1 : 0,
    }).format(distance)} km`
  }

  const updateSlider = (slider, context = {}) => {
    const track = slider.querySelector('[data-vspots-topic-track]')
    if (!track) return

    const city = context.city === undefined ? readSelectedCity() : context.city
    const cityKey = normalizedCityKey(city)
    const location =
      Number.isFinite(context.latitude) && Number.isFinite(context.longitude) ? context : readStoredLocation()
    const showDistances = Boolean(location)
    const cards = Array.from(track.querySelectorAll('[data-vspots-topic-card]'))
    const rankedCards = cards.map((card, originalIndex) => {
      const cityMatches = !cityKey || normalizedCityKey(card.dataset.vspotsTopicCity) === cityKey

      const latitudeValue = String(card.dataset.vspotsTopicLatitude || '').trim()
      const longitudeValue = String(card.dataset.vspotsTopicLongitude || '').trim()
      const latitude = Number(latitudeValue)
      const longitude = Number(longitudeValue)
      const hasCoordinates =
        latitudeValue !== '' && longitudeValue !== '' && hasUsableDealCoordinates(latitude, longitude)
      const distance =
        showDistances && hasCoordinates
          ? distanceInKilometers(location, { latitude, longitude })
          : Number.POSITIVE_INFINITY

      return { card, cityMatches, distance, originalIndex }
    })

    const visibleCards = rankedCards.map((rankedCard) => {
      const { card, cityMatches, distance } = rankedCard
      const isVisible = true
      card.hidden = false

      const badge = card.querySelector('[data-vspots-topic-distance]')
      if (badge && isVisible && Number.isFinite(distance)) {
        badge.textContent = formatDistance(distance)
        badge.hidden = false
      } else if (badge) {
        badge.hidden = true
      }

      return { ...rankedCard, isVisible }
    })

    visibleCards
      .sort(
        (left, right) =>
          Number(right.cityMatches) - Number(left.cityMatches)
          || left.distance - right.distance
          || left.originalIndex - right.originalIndex,
      )
      .forEach(({ card }) => track.append(card))

    const hasMatchingCards = visibleCards.length > 0
    const hasInlinePlacement =
      !slider.hasAttribute('data-vspots-inline-topic-slider') || slider.dataset.vspotsInlineTopicPlaced === 'true'
    slider.hidden = !hasMatchingCards || !hasInlinePlacement
    track.scrollTo({ left: 0, behavior: 'smooth' })
    window.requestAnimationFrame(() => slider.vspotsUpdateButtons?.())
  }

  const placeInlineSlider = (slider) => {
    if (!slider.hasAttribute('data-vspots-inline-topic-slider')) return

    const root = slider.vspotsApiRoot
    const grid = root?.querySelector('[data-vspots-api-grid]')
    if (!grid) return

    const cards = Array.from(grid.children).filter((child) => child.matches('.vspots-deal-card'))
    const columns = window.getComputedStyle(grid).gridTemplateColumns
    const columnCount = Math.max(1, columns.split(/\s+/u).filter(Boolean).length)
    const sliders = [...(inlineSlidersByRoot.get(root) || [slider])]
    const rowOf = (item) => Math.max(1, Number(item.dataset.vspotsInlineTopicRow) || 6)
    const slots = new Map()
    sliders.sort((left, right) => rowOf(left) - rowOf(right)).forEach((item) => {
      // A short first page or city result must not hide an editorial slider.
      const index = Math.min(rowOf(item) * columnCount, cards.length)
      if (!slots.has(index)) slots.set(index, [])
      slots.get(index).push(item)
    })

    slots.forEach((items, index) => {
      let anchor = cards[index] || null
      // Place a shared slot backwards so multiple sliders cannot keep moving
      // each other on every MutationObserver callback (including empty grids).
      items.slice().reverse().forEach((item) => {
        if (item.parentElement !== grid || item.nextElementSibling !== anchor) {
          grid.insertBefore(item, anchor)
        }
        anchor = item
        const wasPlaced = item.dataset.vspotsInlineTopicPlaced === 'true'
        item.dataset.vspotsInlineTopicPlaced = 'true'
        if (!wasPlaced) updateSlider(item)
        else item.vspotsUpdateButtons?.()
      })
    })
  }

  const scheduleInlinePlacement = (slider) => {
    if (slider.vspotsInlinePlacementScheduled) return
    slider.vspotsInlinePlacementScheduled = true
    window.requestAnimationFrame(() => {
      slider.vspotsInlinePlacementScheduled = false
      placeInlineSlider(slider)
    })
  }

  const initializeSlider = (slider) => {
    if (slider.dataset.vspotsTopicInitialized === 'true') return

    const track = slider.querySelector('[data-vspots-topic-track]')
    const previousButton = slider.querySelector('[data-vspots-topic-prev]')
    const nextButton = slider.querySelector('[data-vspots-topic-next]')
    if (!track || !previousButton || !nextButton) return

    slider.dataset.vspotsTopicInitialized = 'true'
    slider.vspotsApiRoot = slider.closest('[data-vspots-api-root]')

    const updateButtons = () => {
      const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth)
      previousButton.disabled = track.scrollLeft <= 2
      nextButton.disabled = track.scrollLeft >= maximumScroll - 2
      slider.classList.toggle('is-static', maximumScroll <= 2)
    }
    slider.vspotsUpdateButtons = updateButtons

    const scroll = (direction) => {
      const card = track.querySelector('.vspots-topic-card:not([hidden])')
      const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 14
      const distance = (card?.getBoundingClientRect().width || track.clientWidth * 0.8) + gap
      track.scrollBy({ left: distance * direction, behavior: 'smooth' })
    }

    previousButton.addEventListener('click', () => scroll(-1))
    nextButton.addEventListener('click', () => scroll(1))
    track.addEventListener('scroll', updateButtons, { passive: true })
    window.addEventListener(
      'resize',
      () => {
        updateButtons()
        scheduleInlinePlacement(slider)
      },
      { passive: true },
    )

    if (slider.hasAttribute('data-vspots-inline-topic-slider')) {
      const grid = slider.vspotsApiRoot?.querySelector('[data-vspots-api-grid]')
      if (grid) {
        const sliders = inlineSlidersByRoot.get(slider.vspotsApiRoot) || new Set()
        sliders.add(slider)
        inlineSlidersByRoot.set(slider.vspotsApiRoot, sliders)
        slider.vspotsInlineObserver = new MutationObserver(() => scheduleInlinePlacement(slider))
        slider.vspotsInlineObserver.observe(grid, { childList: true })
      }
      scheduleInlinePlacement(slider)
    }
    updateSlider(slider)
    updateButtons()
  }

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-vspots-topic-slider]').forEach(initializeSlider)
  }

  document.addEventListener('DOMContentLoaded', () => initializeAll())
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target))
  window.addEventListener('vspots:location-changed', (event) => {
    document.querySelectorAll('[data-vspots-topic-slider]').forEach((slider) => updateSlider(slider, event.detail))
  })
  window.addEventListener('storage', (event) => {
    if (
      event.key !== positionStorageKey
      && event.key !== manualLocationStorageKey
      && event.key !== citySelectionStorageKey
    ) {
      return
    }
    document.querySelectorAll('[data-vspots-topic-slider]').forEach((slider) => updateSlider(slider))
  })
})()
